// The main menu's FEEDBACK screen (spec 2026-10-24): a text box, SEND to its
// right, and "Send feedback to the admins, they might add your ideas" under
// it. SEND emails the message — with the sender's player name — to every
// admin. The box is a real DOM <textarea> laid over the canvas (Phaser has no
// text field), kept on the canvas's FIT-scaled rect, like the CODES box.
//
// Children play this game: the screen asks for no personal details, and the
// server blanks emails and phone numbers before anything is
// sent, and keeps nothing but the time of the last send (CLAUDE.md, rule 2).
import Phaser from 'phaser';
import { accountClient, signedInUserId } from '../account/accountUi';
import { FEEDBACK_MAX, sendFeedback } from '../account/feedback';

export const FEEDBACK_GREEN = 0x2e9e4f;
export const FEEDBACK_GREEN_HOVER = 0x38b85c;
const MINT_HEX = '#b8f0c8';

const PANEL_W = 880;
const PANEL_H = 380;
const D = 50;

/** Open the panel over `scene`; the returned function closes it. */
export function openFeedbackPanel(scene: Phaser.Scene): () => void {
  const W = 1280;
  const H = 720;
  const objs: Phaser.GameObjects.GameObject[] = [];
  const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
    objs.push(o);
    return o;
  };
  let closed = false;

  add(
    scene.add
      .rectangle(W / 2, H / 2, W, H, 0x000000, 0.75)
      .setInteractive()
      .setDepth(D),
  );
  const panelX = (W - PANEL_W) / 2;
  const panelY = (H - PANEL_H) / 2;
  add(
    scene.add
      .rectangle(W / 2, panelY + PANEL_H / 2, PANEL_W, PANEL_H, 0x0f1f15, 1)
      .setStrokeStyle(2, FEEDBACK_GREEN)
      .setDepth(D + 1)
      .setName('feedback-panel'),
  );
  add(
    scene.add
      .text(panelX + 26, panelY + 32, 'FEEDBACK', {
        fontSize: '26px',
        color: MINT_HEX,
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setLetterSpacing(2)
      .setDepth(D + 2),
  );

  // The text box's slot on the canvas (game coordinates).
  const box = { x: panelX + 26, y: panelY + 64, w: 650, h: 170 };
  add(
    scene.add
      .rectangle(box.x + box.w / 2, box.y + box.h / 2, box.w, box.h, 0x07120b, 1)
      .setStrokeStyle(2, 0x2f6b43)
      .setDepth(D + 2),
  );

  // SEND, to the right of the box.
  const send = { x: box.x + box.w + 16, y: box.y, w: PANEL_W - 26 - box.w - 16 - 26, h: 64 };
  const sendBg = add(scene.add.graphics().setDepth(D + 2));
  const drawSend = (hover: boolean, enabled: boolean) => {
    sendBg.clear();
    sendBg.fillStyle(enabled ? (hover ? FEEDBACK_GREEN_HOVER : FEEDBACK_GREEN) : 0x2a3a30, 1);
    sendBg.fillRoundedRect(send.x, send.y, send.w, send.h, 10);
    sendBg.lineStyle(2, 0xffffff, enabled ? 0.85 : 0.3);
    sendBg.strokeRoundedRect(send.x, send.y, send.w, send.h, 10);
  };
  add(
    scene.add
      .text(send.x + send.w / 2, send.y + send.h / 2, 'SEND', {
        fontSize: '24px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setDepth(D + 3),
  );
  const sendHit = add(
    scene.add
      .zone(send.x + send.w / 2, send.y + send.h / 2, send.w, send.h)
      .setDepth(D + 4)
      .setName('feedback-send')
      .setInteractive({ useHandCursor: true }),
  );

  const counter = add(
    scene.add
      .text(box.x + box.w, box.y + box.h + 16, `0 / ${FEEDBACK_MAX}`, {
        fontSize: '13px',
        color: '#8fa89a',
      })
      .setOrigin(1, 0.5)
      .setDepth(D + 2)
      .setName('feedback-counter'),
  );
  add(
    scene.add
      .text(box.x, box.y + box.h + 22, 'Send feedback to the admins, they might add your ideas', {
        fontSize: '18px',
        color: '#ffffff',
      })
      .setOrigin(0, 0.5)
      .setDepth(D + 2)
      .setName('feedback-blurb'),
  );
  add(
    scene.add
      .text(
        box.x,
        box.y + box.h + 50,
        "Don't type your real name, email, address or phone number — they are removed.",
        { fontSize: '14px', color: '#ffcf6b', fontStyle: 'italic' },
      )
      .setOrigin(0, 0.5)
      .setDepth(D + 2)
      .setName('feedback-safety'),
  );
  const message = add(
    scene.add
      .text(box.x, box.y + box.h + 84, '', {
        fontSize: '17px',
        color: '#ff8a8a',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(D + 2)
      .setName('feedback-message'),
  );

  const closeBtn = add(
    scene.add
      .text(panelX + PANEL_W - 30, panelY + 32, '✕', {
        fontSize: '26px',
        color: '#cfd3dc',
        padding: { x: 10, y: 4 },
      })
      .setOrigin(0.5)
      .setDepth(D + 2)
      .setInteractive({ useHandCursor: true })
      .setName('feedback-close'),
  );
  closeBtn.on('pointerover', () => closeBtn.setColor(MINT_HEX));
  closeBtn.on('pointerout', () => closeBtn.setColor('#cfd3dc'));

  // ── the DOM text box ──────────────────────────────────────────────────────
  const input = document.createElement('textarea');
  input.maxLength = FEEDBACK_MAX;
  input.autocomplete = 'off';
  input.spellcheck = true;
  input.placeholder = 'Type your idea or what went wrong…';
  input.dataset.testid = 'feedback-input';
  input.setAttribute('aria-label', 'Feedback');
  Object.assign(input.style, {
    position: 'fixed',
    zIndex: '20',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    resize: 'none',
    color: '#ffffff',
    fontFamily: 'system-ui, sans-serif',
    padding: '10px 14px',
    boxSizing: 'border-box',
  } satisfies Partial<CSSStyleDeclaration>);
  // Phaser listens on the window: a click or a key in the box must not also
  // reach the game underneath.
  for (const ev of ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'touchstart', 'touchend']) {
    input.addEventListener(ev, (e) => e.stopPropagation());
  }
  // Wheel too, so scrolling a long message never scrolls a list behind it.
  input.addEventListener('wheel', (e) => e.stopPropagation());
  input.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Escape') close();
  });
  input.addEventListener('keyup', (e) => e.stopPropagation());
  input.addEventListener('input', () => counter.setText(`${input.value.length} / ${FEEDBACK_MAX}`));
  // Into the game's own container, so it still shows in fullscreen.
  (scene.game.canvas.parentElement ?? document.body).append(input);

  const place = () => {
    const rect = scene.game.canvas.getBoundingClientRect();
    const s = rect.width / W;
    input.style.left = `${rect.left + (box.x + 2) * s}px`;
    input.style.top = `${rect.top + (box.y + 2) * s}px`;
    input.style.width = `${(box.w - 4) * s}px`;
    input.style.height = `${(box.h - 4) * s}px`;
    input.style.fontSize = `${Math.max(12, 19 * s)}px`;
  };
  place();
  window.addEventListener('resize', place);
  scene.scale.on(Phaser.Scale.Events.RESIZE, place);

  // ── state ─────────────────────────────────────────────────────────────────
  const client = accountClient();
  const signedIn = client !== null && signedInUserId() !== null;
  let busy = false;
  const setEnabled = (on: boolean) => {
    input.disabled = !on;
    drawSend(false, on);
  };
  setEnabled(signedIn);
  if (signedIn) input.focus();
  else message.setColor('#ffcf6b').setText('Sign in to send feedback.');

  async function submit(): Promise<void> {
    if (!client || !signedIn || busy) return;
    const text = input.value.trim();
    if (text === '') {
      message.setColor('#ff8a8a').setText('Type your feedback first.');
      return;
    }
    busy = true;
    setEnabled(false);
    message.setColor('#cfd3dc').setText('Sending…');
    const r = await sendFeedback(client, text);
    busy = false;
    if (closed) return;
    setEnabled(true);
    if (r.kind === 'sent') {
      input.value = '';
      counter.setText(`0 / ${FEEDBACK_MAX}`);
      message.setColor('#8ef0a0').setText('Thank you! Your feedback was sent to the admins.');
    } else if (r.kind === 'wait') {
      const mins = Math.max(1, Math.ceil(r.seconds / 60));
      message
        .setColor('#ffcf6b')
        .setText(`You can send more feedback in ${mins} minute${mins === 1 ? '' : 's'}.`);
    } else if (r.kind === 'unavailable') {
      message.setColor('#ff8a8a').setText("Feedback can't be sent right now — try again later.");
    } else {
      message.setColor('#ff8a8a').setText('Could not send — try again.');
    }
  }

  sendHit.on('pointerover', () => drawSend(true, !input.disabled));
  sendHit.on('pointerout', () => drawSend(false, !input.disabled));
  sendHit.on('pointerup', () => void submit());

  function close(): void {
    if (closed) return;
    closed = true;
    window.removeEventListener('resize', place);
    scene.scale.off(Phaser.Scale.Events.RESIZE, place);
    input.remove();
    objs.forEach((o) => o.destroy());
  }
  closeBtn.on('pointerup', close);
  scene.events.once(Phaser.Scenes.Events.SHUTDOWN, close);
  return close;
}
